import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Loader2, Calendar, Check, Unlink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleCalendarConnect() {
  const [status, setStatus] = useState(null); // { connected, email, configured }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/calendar/google/status`, { credentials: "include" });
      if (res.ok) setStatus(await res.json());
    } catch {}
  };

  useEffect(() => {
    load();
    // Surface the result of the OAuth callback redirect
    const params = new URLSearchParams(window.location.search);
    const g = params.get("google_status");
    if (g === "connected") {
      toast.success("Google Calendar connected — new bookings will sync automatically");
    } else if (g === "denied") {
      toast.error("Google Calendar connection denied");
    } else if (g === "error") {
      toast.error("Couldn't connect Google Calendar — try again");
    }
    if (g) {
      // strip the query so refreshes don't re-toast
      params.delete("google_status");
      const clean = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`;
      window.history.replaceState({}, "", clean);
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/calendar/google/connect`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to start OAuth");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Google Calendar? Future bookings won't sync.")) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/calendar/google/disconnect`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Disconnect failed");
      toast.success("Google Calendar disconnected");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <Button variant="outline" size="sm" disabled>
        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Checking…
      </Button>
    );
  }

  if (status.connected) {
    return (
      <div className="flex items-center gap-1.5" data-testid="google-cal-connected">
        <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
          <Check className="w-3 h-3" />
          Google synced{status.email ? ` (${status.email})` : ""}
        </Badge>
        <Button
          variant="ghost" size="sm" className="h-7 px-2 text-slate-500 hover:text-red-600"
          onClick={disconnect}
          disabled={busy}
          title="Disconnect Google Calendar"
          data-testid="google-cal-disconnect-btn"
        >
          <Unlink className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="outline" size="sm"
      onClick={connect}
      disabled={busy || !status.configured}
      data-testid="google-cal-connect-btn"
      title={status.configured ? "" : "Google OAuth not configured"}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Calendar className="w-3.5 h-3.5 mr-1" />}
      Connect Google Calendar
    </Button>
  );
}
